/**
 * Shared rules for the "attach a song" file pickers (landing composer,
 * DirectorHome composer).
 *
 * Two things were turning first-time users away before they ever made a video
 * (2026-07-25 support thread):
 *
 *  1. The cap was 50MB, which is ~5 minutes of WAV. Nothing downstream needs
 *     that: uploads go straight to R2 via a presigned PUT, and the director
 *     re-encodes anything over DIRECTOR_ANALYSIS_MAX_AUDIO_BYTES to a compact
 *     MP3 before analysis. The cap only existed to bound the API relay
 *     fallback, so it now matches that relay's real limit.
 *
 *  2. accept="audio/*" hides .mp4/.mov in the iPadOS Files picker. A lot of
 *     users only have their song as a video (iPad exports, screen recordings,
 *     downloaded clips), so "attach a song" was unreachable on an iPad. We
 *     accept video containers and let the server strip the video stream.
 *
 * Keep MAX_AUDIO_UPLOAD_MB in sync with DIRECT_UPLOAD_MAX_BYTES in
 * server/_core/index.ts.
 */

export const MAX_AUDIO_UPLOAD_MB = 200;
export const MAX_AUDIO_UPLOAD_BYTES = MAX_AUDIO_UPLOAD_MB * 1024 * 1024;

/**
 * iPadOS/iOS report an empty or wrong `file.type` for plenty of files, and the
 * Files picker greys out anything the accept list doesn't match. Listing bare
 * extensions alongside the MIME globs is what actually makes .mp4/.m4a
 * selectable there.
 */
export const AUDIO_UPLOAD_ACCEPT = [
  "audio/*",
  "video/mp4",
  "video/quicktime",
  "video/webm",
  ".mp3",
  ".wav",
  ".m4a",
  ".aac",
  ".ogg",
  ".oga",
  ".opus",
  ".flac",
  ".aiff",
  ".aif",
  ".caf",
  ".mp4",
  ".m4v",
  ".mov",
  ".webm",
].join(",");

const ACCEPTED_EXTENSION_RE =
  /\.(mp3|wav|m4a|aac|ogg|oga|opus|flac|aiff|aif|caf|mp4|m4v|mov|webm|mkv|3gp)$/i;

/**
 * Whether a dropped/picked file is something we can pull audio out of. Checks
 * the extension as well as the MIME type because Safari hands us `""` for
 * files that came from iCloud Drive.
 */
export function isAcceptedAudioSourceFile(file: File): boolean {
  if (/^(audio|video)\//i.test(file.type)) return true;
  return ACCEPTED_EXTENSION_RE.test(file.name);
}

/**
 * Audio-only variant for tools whose backend cannot demux video (stem
 * splitter / vocal remover feeds the file straight to demucs). Bare
 * extensions are still listed so the iPadOS Files picker does not grey out
 * files with an empty or wrong MIME type.
 */
export const AUDIO_ONLY_UPLOAD_ACCEPT =
  "audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac";

const AUDIO_ONLY_EXTENSION_RE =
  /\.(mp3|wav|m4a|aac|ogg|oga|opus|flac|aiff|aif|caf|webm)$/i;

/**
 * Like isAcceptedAudioSourceFile but rejects video containers. Falls back to
 * the extension when `file.type` is empty or not an audio MIME type, which is
 * what iPadOS/iCloud Drive report for many files.
 */
export function isAcceptedAudioOnlyFile(file: File): boolean {
  if (/^audio\//i.test(file.type)) return true;
  if (/^video\//i.test(file.type)) return false;
  return AUDIO_ONLY_EXTENSION_RE.test(file.name);
}
